import { useEffect, useState } from "react";
import { ExternalLink } from "lucide-react";

import type { GrantOpportunity, GrantOpportunityPayload } from "@/api/grant_opportunities";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useCurrentUser } from "@/context/UserProvider";
import { userIsOutsideUser } from "@/lib/grantSuiteAccess";

type PortalField = "funder_portal_name" | "funder_portal_url" | "funder_portal_notes";

type FunderPortalCardProps = {
  opportunity: GrantOpportunity;
  onUpdate: (patch: Partial<GrantOpportunityPayload>) => void;
  updating: boolean;
  // The patch the last rejected save tried to write, a fresh object per
  // failure. Only the fields it names roll back, so a rejection elsewhere in
  // the detail pane leaves portal drafts alone.
  rejectedUpdate?: Partial<GrantOpportunityPayload> | null;
};

// Empty means "cleared", matching what a commit sends and what the server
// stores, so a draft can be compared against either.
function normalize(draft: string): string | null {
  return draft.trim() === "" ? null : draft.trim();
}

// A draft that re-syncs only when ITS server value (or the opportunity)
// changes: committing one field must not wipe a sibling draft the user is
// still typing in, but a focus+blur with no edits can still never commit a
// stale value over a teammate's save.
function useSyncedDraft(
  opportunityId: number,
  field: PortalField,
  serverValue: string | null,
  rejectedUpdate: Partial<GrantOpportunityPayload> | null | undefined,
) {
  const [draft, setDraft] = useState(serverValue ?? "");

  useEffect(() => {
    setDraft(serverValue ?? "");
  }, [opportunityId, serverValue]);

  // A rejected save leaves the server value untouched, so the sync above never
  // fires and the input would keep showing text that was never saved
  // (ENG-1872). Roll back only while the input still holds the refused text:
  // a 422 on a fixable URL invites an immediate retype, and that correction
  // outranks a rollback landing behind it.
  useEffect(() => {
    if (!rejectedUpdate || !(field in rejectedUpdate)) return;
    const refused = rejectedUpdate[field] ?? null;
    setDraft((current) => (normalize(current) === refused ? (serverValue ?? "") : current));
  }, [rejectedUpdate, field, serverValue]);

  return [draft, setDraft] as const;
}

// Reference-only record of the funder's portal (ENG-1819): where staff go to
// submit, never something the product calls or submits to. The link below is
// the only outbound touchpoint, and it's a plain anchor. Inputs stay enabled
// while a save is in flight — blur only commits actual changes, and disabling
// mid-edit would eject focus and eat keystrokes.
export function FunderPortalCard({
  opportunity,
  onUpdate,
  rejectedUpdate,
}: FunderPortalCardProps) {
  const currentUser = useCurrentUser();
  // View-only for outside collaborators (ENG-1877); with nothing recorded the
  // card would be three empty rows, so it drops out entirely.
  const outsideUser = userIsOutsideUser(currentUser);
  const [nameDraft, setNameDraft] = useSyncedDraft(
    opportunity.id,
    "funder_portal_name",
    opportunity.funder_portal_name,
    rejectedUpdate,
  );
  const [urlDraft, setUrlDraft] = useSyncedDraft(
    opportunity.id,
    "funder_portal_url",
    opportunity.funder_portal_url,
    rejectedUpdate,
  );
  const [notesDraft, setNotesDraft] = useSyncedDraft(
    opportunity.id,
    "funder_portal_notes",
    opportunity.funder_portal_notes,
    rejectedUpdate,
  );

  const commit = (field: PortalField, draft: string) => {
    const next = normalize(draft);
    if (next === opportunity[field]) return;
    onUpdate({ [field]: next });
  };

  if (outsideUser) {
    const hasAny =
      opportunity.funder_portal_name ||
      opportunity.funder_portal_url ||
      opportunity.funder_portal_notes;
    if (!hasAny) return null;

    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Funder portal</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          {opportunity.funder_portal_name && (
            <div className="space-y-1">
              <Label>Portal name</Label>
              <p className="font-medium">{opportunity.funder_portal_name}</p>
            </div>
          )}
          {opportunity.funder_portal_url && (
            <div className="space-y-1">
              <Label>Portal URL</Label>
              <a
                href={opportunity.funder_portal_url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-primary hover:underline"
              >
                Open portal
                <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          )}
          {opportunity.funder_portal_notes && (
            <div className="space-y-1">
              <Label>Portal notes</Label>
              <p className="whitespace-pre-wrap break-words">{opportunity.funder_portal_notes}</p>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Funder portal</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <div className="space-y-2">
          <Label htmlFor="funder-portal-name">Portal name</Label>
          <Input
            id="funder-portal-name"
            placeholder="e.g. Grants.gov Workspace"
            value={nameDraft}
            onChange={(event) => setNameDraft(event.target.value)}
            onBlur={() => commit("funder_portal_name", nameDraft)}
          />
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="funder-portal-url">Portal URL</Label>
            {opportunity.funder_portal_url && (
              <a
                href={opportunity.funder_portal_url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
              >
                Open portal
                <ExternalLink className="h-3 w-3" />
              </a>
            )}
          </div>
          <Input
            id="funder-portal-url"
            type="url"
            placeholder="https://…"
            value={urlDraft}
            onChange={(event) => setUrlDraft(event.target.value)}
            onBlur={() => commit("funder_portal_url", urlDraft)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="funder-portal-notes">Portal notes</Label>
          <Textarea
            id="funder-portal-notes"
            placeholder="Who owns portal access, submission steps, deadline quirks…"
            value={notesDraft}
            onChange={(event) => setNotesDraft(event.target.value)}
            onBlur={() => commit("funder_portal_notes", notesDraft)}
          />
        </div>
      </CardContent>
    </Card>
  );
}
